import { useEffect, useRef, useState } from 'preact/hooks'
import { FileText, Pencil, Save, X, Globe, Upload, Loader2, RefreshCw } from 'lucide-preact'
import { websApi, type WebViewDto } from '@/api/webs'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { TextBoxField } from '@/components'
import { RichTextBox } from '@/components/common/RichTextBox'
import { Controller } from 'react-hook-form'
import { MarkdownView } from '@/components/common/MarkdownView'
import { DetailRow } from './DetailRow'
import { CopyButton } from '@/components/common/CopyButton'
import { useAIForm, handleListFieldUpdate } from '@/hooks/useAIForm'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useBenefitsList } from '@/hooks/useBenefitsList'
import { BenefitsCard } from './BenefitsCard'

export function DownloadPanel({ web, onPatchWeb, isSignedOff, routeToTab }: { web: WebViewDto; onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void; isSignedOff?: boolean; routeToTab?: (tab: WebTabKey) => void }) {
  const download = web.Download
  const { showSuccess, showError } = useToast()
  const { canEdit } = useWorkspaceCapabilities()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingBenefits, setEditingBenefits] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  type DownloadForm = { title: string; overview: string; benefits: { value: string }[] }

  const { form } = useAIForm<DownloadForm>({
    mode: 'onChange',
    defaultValues: { title: download?.Title || '', overview: download?.Overview || '', benefits: (download?.Benefits || []).map(b => ({ value: b })) }
  }, {
    aiContext: { campaignId: web.CampaignId, webId: web.Id, location: ChatLocations.WebDownloadEditor, entityType: ChatEntityTypes.Web, entityId: web.Id, viewMode: editing || editingBenefits ? ChatViewModes.Edit : ChatViewModes.View },
    fields: [
      { aiName: 'downloadTitle', formPath: 'title', displayName: 'Download Title', fieldType: 'text', maxLength: 200, isEditable: editing },
      { aiName: 'downloadOverview', formPath: 'overview', displayName: 'Download Overview', fieldType: 'textarea', maxLength: 5000, isEditable: editing },
      { aiName: 'downloadBenefits', formPath: 'benefits', displayName: 'Why Download Benefits', fieldType: 'textarea', isEditable: editingBenefits, isListField: true },
    ],
    onFieldUpdate: (aiName, value) => {
      if (aiName === 'downloadBenefits') handleListFieldUpdate(value, benefits.append)
    },
  })

  const benefits = useBenefitsList({
    form,
    serverBenefits: download?.Benefits || [],
    editing: editingBenefits,
    setEditing: setEditingBenefits,
    onSave: async (clean) => {
      try {
        const res = await websApi.updateDownloadBenefits(web.WorkspaceId, web.Id, { ETag: web.ETag, Benefits: clean })
        if (!res.error && res.data) { onPatchWeb(prev => ({ ...prev, ...res.data })); showSuccess('Updated', 'Why Download list saved.'); return true }
        if (res.status !== 422) showError('Save failed', res.error || 'Unable to update Why Download list')
        return false
      } catch { showError('Save failed', 'Network or server error'); return false }
    },
  })

  const { watch, reset, control } = form

  useEffect(() => { reset({ title: download?.Title || '', overview: download?.Overview || '', benefits: (download?.Benefits || []).map(b => ({ value: b })) }) }, [download?.Title, download?.Overview, download?.Benefits])

  const w = watch()
  const initial = { title: download?.Title || '', overview: download?.Overview || '' }
  const current = { title: (w.title || '').trim(), overview: (w.overview || '').trim() }
  const isChanged = JSON.stringify(initial) !== JSON.stringify(current)
  const save = async () => { setSaving(true); try { const dto = { ETag: web.ETag, Title: current.title || undefined, Overview: current.overview || undefined }; const res = await websApi.updateDownloadDetails(web.WorkspaceId, web.Id, dto); if (!res.error && res.data) { onPatchWeb(prev => ({ ...prev, ...res.data })); showSuccess('Updated', 'Download details saved.'); setEditing(false) } else if (res.status !== 422) showError('Save failed', res.error || 'Unable to update download details') } catch { showError('Save failed', 'Network or server error') } setSaving(false) }
  const cancel = () => { setEditing(false); reset() }

  const acceptedExtensions = ['.pdf', '.docx', '.xlsx', '.pptx', '.zip']
  const isAcceptedFile = (file: File) => acceptedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }
  const handleFileUpload = async (file: File) => {
    if (!isAcceptedFile(file)) { setUploadError('File type not accepted. Use PDF, DOCX, XLSX, PPTX, or ZIP.'); return }
    setUploadError(null)
    setUploading(true)
    try {
      const res = await websApi.uploadDownloadFile(web.WorkspaceId, web.Id, file)
      if (!res.error && res.data) { onPatchWeb(prev => ({ ...prev, ...res.data })); showSuccess('Uploaded', `${file.name} uploaded successfully.`) }
      else { setUploadError(res.error || 'Upload failed') }
    } catch { setUploadError('Network or server error') }
    setUploading(false)
  }
  const handleFileInput = (e: Event) => { const input = e.target as HTMLInputElement; if (input.files?.[0]) handleFileUpload(input.files[0]); input.value = '' }
  const handleDrop = (e: DragEvent) => { e.preventDefault(); setDragOver(false); const file = e.dataTransfer?.files?.[0]; if (file) handleFileUpload(file) }

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left column: Download details */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><FileText className="w-5 h-5 text-primary-600" /> Download details</h2>
          {!editing ? (
            canEdit && <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><Pencil className="w-4 h-4" /> Edit</button>
          ) : (
            <div className="flex items-center gap-2">
              <button type="button" disabled={saving || !isChanged || (w.title.trim().length === 0)} onClick={save} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
              <button type="button" onClick={cancel} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
            </div>
          )}
        </div>
        {!editing ? (
          <div className="mt-3 space-y-3 text-[13px]">
            <DetailRow label="Title" value={download?.Title || '—'} />
            <DetailRow label="Overview" value={download?.Overview ? <MarkdownView>{download.Overview}</MarkdownView> : '—'} />
            <DetailRow label="Internal Form ID" value={web.InternalFormBindingId ? (
              <span className="inline-flex items-center gap-2"><code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-800 dark:text-slate-100">{web.InternalFormBindingId}</code><CopyButton text={web.InternalFormBindingId} label="Form ID" /></span>
            ) : '—'} />
          </div>
        ) : (
          <form className="mt-3 space-y-3" onSubmit={e => { e.preventDefault() }}>
            <TextBoxField control={control} name="title" labelSize="xs" label="Title" showCharCounter maxLength={200} rules={{ validate: v => (v ?? '').trim().length > 0 || 'Required' }} placeholder="Title" />
            <Controller name="overview" control={control} render={({ field }) => (<RichTextBox name="overview" label="Overview" labelSize="xs" value={field.value} onChange={field.onChange} onBlur={field.onBlur} placeholder="Optional overview" minHeight="120px" />)} />
          </form>
        )}
      </div>
      {/* Right column: Why Download */}
      <BenefitsCard title="Why Download" canEdit={canEdit} {...benefits} />
    </div>
    {/* Download file upload */}
    <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Upload className="w-5 h-5 text-primary-600" /> Download File</h3>
        {download?.FileName && !uploading && canEdit && (
          <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            <RefreshCw className="w-4 h-4" /> Replace
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept=".pdf,.docx,.xlsx,.pptx,.zip" className="hidden" onChange={handleFileInput} />
      {uploading ? (
        <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" /> Uploading...
        </div>
      ) : download?.FileName ? (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
          <FileText className="w-8 h-8 text-primary-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{download.FileName}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {download.FileSizeBytes != null && <span className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(download.FileSizeBytes)}</span>}
              {download.FileContentType && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{download.FileContentType}</span>}
            </div>
          </div>
        </div>
      ) : (
        canEdit ? (
          <div
            className={`flex flex-col items-center justify-center gap-2 py-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${dragOver ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <Upload className="w-8 h-8 text-slate-400 dark:text-slate-500" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Drag and drop or click to upload</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">PDF, DOCX, XLSX, PPTX, ZIP</p>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">No file uploaded</p>
        )
      )}
      {uploadError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{uploadError}</p>}
    </div>
    {isSignedOff && routeToTab && (
      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Landing Page</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Generate a landing page for this download to drive registrations.
            </p>
          </div>
          <button
            type="button"
            onClick={() => routeToTab('media')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Globe className="w-4 h-4" />
            Generate Landing Page
          </button>
        </div>
      </div>
    )}
    </>
  )
}
